<template>
  <v-dialog
    :value="true"
    max-width="1536px"
    scrollable
    @click:outside="handleClose"
  >
    <v-card>
      <v-card-title>
        <span>{{ bucketData.bucket_name }} A/B Tests </span>

        <v-spacer />

        <v-btn
          icon
          @click="handleClose"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider />

      <v-card-text class="bg-pale-green">
        <v-data-table
          dense
          :headers="headers"
          :loading="loading"
          :items="items"
          :options.sync="options"
          :server-items-length="totalItems"
          :footer-props.sync="footerProps"
          @update:options="updateTable()"
        >
          <template #top>
            <v-card-title>
              <v-toolbar flat>
                <v-text-field
                  v-model="search"
                  append-icon="mdi-magnify"
                  label="Search"
                  single-line
                  hide-details
                  @input="searchTable()"
                />

                <v-spacer />

                <v-btn
                  v-if="hasPermission('edit ab-testing::ab-tests')"
                  dark
                  class="mb-2 mr-2 btn-bg-regal-gray"
                  @click="openPriorityModal = true"
                >
                  <v-icon>mdi-playlist-edit</v-icon>
                  A/B Test Priorities
                </v-btn>
                <DisabledForLackOfRoleBtn
                  v-else
                  text="A/B Test Priorities"
                  :x-small="false"
                  button-class="mx-2"
                />

                <v-btn
                  v-if="hasPermission('create ab-testing::ab-tests')"
                  dark
                  class="mb-2 btn-bg-deep-blue"
                  @click="handleViewAbDetails()"
                >
                  <v-icon>mdi-plus</v-icon>
                  New A/B Test
                </v-btn>
                <DisabledForLackOfRoleBtn
                  v-else
                  text="New A/B Test"
                  :x-small="false"
                  button-class="mx-2"
                />
              </v-toolbar>
            </v-card-title>
          </template>

          <template #item.cap_enabled="{ item }">
            {{ item.cap_enabled ? 'Yes' : 'No' }}
          </template>

          <template #item.is_active="{ item }">
            {{ item.is_active ? 'Yes' : 'No' }}
          </template>

          <template #item.actions="{ item }">
            <v-btn
              v-if="hasPermission('edit ab-testing::ab-tests')"
              class="btn-bg-deep-blue"
              text-color="white"
              @click="handleViewAbDetails(item)"
            >
              <v-icon left>
                mdi-eye
              </v-icon>
              Details
            </v-btn>
            <DisabledForLackOfRoleBtn
              v-else
              text="Details"
              :x-small="false"
              button-class="mx-2"
            />
          </template>
        </v-data-table>

        <AbTestDetails
          v-if="openDetailsModal"
          :bucket-id="bucketData.id"
          :bucket-name="bucketData.bucket_name"
          :ab-test-id="activeabTestId"
          @close="openDetailsModal = false"
          @saved="handleAbTestSaved"
        />

        <AbTestEvaluationOrderModal
          v-if="openPriorityModal"
          :bucket-id="bucketData.id"
          :bucket-name="bucketData.bucket_name"
          @close="openPriorityModal = false"
          @updated="handlePriorityUpdated"
        />
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script setup>
import dataTableMixin from '@/mixins/dataTableMixin';
import DisabledForLackOfRoleBtn from '@/views/DisabledForLackOfRoleBtn.vue';
import AbTestDetails from './AbTestDetails.vue';
import AbTestEvaluationOrderModal from './AbTestEvaluationOrderModal.vue';
</script>

<script>
export default {
  name: 'AbTestList',

  mixins: [dataTableMixin],

  props: {
    bucketData: {
      type: Object,
      required: true,
      default: () => ({
        id: null,
        bucket_name: '',
        bucket_type: '',
        testing_ratio: '',
        default_ratio: '',
        description: '',
        is_active: false,
      }),
    },
  },

  emits: ['close'],

  data: () => ({
    headers: [
      { text: 'ID', value: 'id' },
      { text: 'A/B Test Name', value: 'test_name' },
      { text: 'Champion Strategy', value: 'champion_strategy.name' },
      { text: 'Cap Enabled ?', value: 'cap_enabled' },
      { text: 'Cap. Unit', value: 'cap_unit_label' },
      { text: 'Cap. Value', value: 'cap_value' },
      { text: 'Freq. Days', value: 'cap_frequency_days' },
      { text: 'Eval. Order', value: 'evaluation_order' },
      { text: 'Is Active ?', value: 'is_active' },
      { text: '', value: 'actions', sortable: false },
    ],

    openDetailsModal: false,
    activeabTestId: null,
    selectedAbTest: {},
    openPriorityModal: false,
  }),

  watch: {
    abTests: {
      immediate: true,
      handler(newVal) {
        this.items = newVal;
      },
    },
  },

  methods: {
    async updateTable() {
      this.loading = true;

      try {
        const res = await this.$axios.get('api/v1/ab-tests', {
          params: {
            perPage: this.options.itemsPerPage,
            page: this.options.page,
            sortBy: this.options.sortBy,
            sortDesc: this.options.sortDesc,
            search: this.search,
            bucket_id: this.bucketData.id,
          },
        });

        this.items = res.data.data;
        this.totalItems = res.data.total;
      } catch (error) {
        this.setNotification({
          color: 'red',
          message: 'Error fetching bucket abTests',
        });
      } finally {
        this.loading = false;
      }
    },

    handleViewAbDetails(item = null) {
      this.activeabTestId = item?.id || null;
      this.openDetailsModal = true;
    },

    handleAbTestSaved() {
      this.updateTable();
    },

    handlePriorityUpdated() {
      this.updateTable();
      this.openPriorityModal = false;
    },

    handleClose() {
      this.$emit('close');
    },
  },
};
</script>
